interface PromoRules {
  included_variant_ids: number[];
  excluded_product_ids: number[];
  included_product_ids: number[];
}

export interface Tier {
  price_range: number[];
  percent: number;
}

interface PromoDiscount {
  flat_percent: number | null;
  flat_rate: number | null;
  tiered: Tier[] | null;
}

export interface Promo {
  id: number;
  code: string;
  type: 'variant' | 'product';
  description: string;
  rules: PromoRules;
  discounts: PromoDiscount;
  starts_at: string;
  expires_at: string;
}

export interface MembershipPromo {
  promotion_code: string;
  membership?: {
    slug: string;
    price: number;
    promotional_price: number;
  };
}

export type Price = number | string;

const getTieredDiscount = (price: Price, promo: Promo) => {
  const tieredDiscounts = promo?.discounts?.tiered;
  if (price && tieredDiscounts && tieredDiscounts.length > 0) {
    return tieredDiscounts.reduce((acc, discount) => {
      const { price_range, percent } = discount;
      const min = +price_range[0];
      const max = price_range[1] !== null ? +price_range[1] : Infinity;
      if (+price > min && +price <= max) {
        acc = percent;
      }
      return acc;
    }, 0);
  }
  return 0;
};

const getDiscount = (price: Price, promo?: Promo | null) => {
  if (!promo) {
    return 0;
  }

  if (promo.discounts.flat_rate) {
    return promo.discounts.flat_rate;
  }

  if (promo.discounts.flat_percent) {
    return promo.discounts.flat_percent;
  }

  if (promo.discounts.tiered?.length && price) {
    return getTieredDiscount(price, promo);
  }

  return 0;
};

/**
 * Format total price as whole number without trailing zeros or show price with cents
 */
const formatPrice = (price: number): string =>
  Number.isInteger(price) ? price.toString() : (+price).toFixed(2);

const calculateDiscountedPrice = (
  price: Price,
  type: 'percent' | 'flat_rate',
  discount: number,
): number => {
  if (discount !== 0) {
    switch (type) {
      case 'percent':
        const totalDiscount = +price * (+discount / 100);

        return +price - +totalDiscount;
      case 'flat_rate':
        return +price - +discount;
      default:
        return +price;
    }
  }

  return +price;
};

export const filterActivePromos = (promos: Promo[]): Promo[] => {
  const now = new Date();

  return promos.filter((promo) => {
    const startsAt = new Date(promo.starts_at);
    const expiresAt = new Date(promo.expires_at);
    return startsAt <= now && now <= expiresAt;
  });
};

/**
 * Returns true if the given promo is applicable to a given product. It is
 * applicable if the promo is explicitly included in the promo rules, or
 * is at least not explicitly excluded.
 * @param productId ID of the given product
 * @param promo the promo to check
 * @param promotionable attribute describing whether the given product can have promos applied in general
 */
export const promoAppliesToProduct = (
  productId: number,
  promo: Promo,
  promotionable?: boolean,
): boolean =>
  promotionable !== false &&
  !promo.rules.excluded_product_ids.includes(productId) &&
  (promo.rules.included_product_ids.includes(productId) ||
    promo.rules.included_product_ids.length === 0);

/**
 * Finds the active promotion for a given product.
 *
 * This function searches through a list of promotions and returns the first promotion
 * that applies to the specified product and is currently active.
 *
 * A promotion is considered to apply to a product if:
 * - The product ID is included in the promotion's `included_product_ids` array, or
 * - The `included_product_ids` array is empty (indicating the promotion applies to all products), and
 * - The product ID is not included in the promotion's `excluded_product_ids` array.
 *
 * A promotion is considered active if:
 * - The current date is between the promotion's `starts_at` and `expires_at` dates.
 *
 * @param {number} productId - The ID of the product to check for active promotions.
 * @param {Promo[]} promos - An array of promotions to search through.
 * @returns {Promo | undefined} - The first promotion that applies to the product and is currently active, or undefined if no such promotion is found.
 */
export const findActivePromotionForProduct = (
  productId: number,
  promos: Promo[],
) =>
  promos.find((promo) => {
    const doesPromoApplyToProduct =
      (promo.rules?.included_product_ids?.includes(productId) ||
        (promo.rules?.included_product_ids?.length ?? 0) === 0) &&
      !promo.rules?.excluded_product_ids?.includes(productId);

    let isPromoActive = false;
    if (promo.starts_at && promo.expires_at) {
      const startsAt = new Date(promo.starts_at);
      const expiresAt = new Date(promo.expires_at);
      isPromoActive = startsAt <= new Date() && new Date() <= expiresAt;
    }

    return doesPromoApplyToProduct && isPromoActive;
  });

/**
 * Return base price, or discounted price if promo is applicable
 */
export const discountedPrice = (
  basePrice: Price,
  promo?: Promo | null,
): string => {
  const discountAmount = getDiscount(basePrice, promo);

  const discountType =
    promo?.discounts.flat_percent || promo?.discounts.tiered?.length
      ? 'percent'
      : 'flat_rate';

  const discountedPrice = calculateDiscountedPrice(
    basePrice,
    discountType,
    discountAmount,
  );

  return formatPrice(discountedPrice);
};

/**
 * Generates a mock promo object with start and end dates that increment by one day each time it is called.
 *
 * The start date of the first promo is the current date, and each subsequent promo starts one day after the last promo's end date.
 *
 * @returns {Promo} A mock promo object with incremented start and end dates.
 *
 * @example
 * const promo1 = buildPromoMock();
 * console.log(promo1.starts_at); // Today's date
 * console.log(promo1.expires_at); // Tomorrow's date
 *
 * const promo2 = buildPromoMock();
 * console.log(promo2.starts_at); // Day after tomorrow's date
 * console.log(promo2.expires_at); // Two days after tomorrow's date
 */
export const buildPromoMock = (() => {
  let lastEndDate: Date | null = null;

  return (): Promo => {
    const today = lastEndDate ? new Date(lastEndDate) : new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const randomCode = Math.random().toString(36).substring(7);

    const promo: Promo = {
      id: Math.floor(Math.random() * 1000) + 1,
      code: randomCode,
      description: `promo description ${randomCode}`,
      type: 'product' as const,
      rules: {
        included_variant_ids: [],
        excluded_product_ids: [9, 8],
        included_product_ids: [1, 2, 3],
      },
      discounts: {
        flat_percent: 50,
        flat_rate: null,
        tiered: [
          {
            price_range: [10, 15],
            percent: 18,
          },
        ],
      },
      starts_at: today.toDateString(),
      expires_at: tomorrow.toDateString(),
    };

    lastEndDate = tomorrow;

    return promo;
  };
})();

/**
 * Calculates the discounted promotional price based on the given price and active promotion.
 *
 * @param price - The original price as a string or null.
 * @param activePromo - The active promotion object or undefined.
 * @returns The discounted promotional price as a number. If the price is null or invalid, returns 0.
 */
export const calculateDiscountedPromoPrice = (
  price: string | null,
  activePromo: Promo | undefined,
): number => {
  const discountedPromoPriceCalc = parseFloat(
    discountedPrice(price ?? '0', activePromo ? activePromo : undefined),
  );

  return discountedPromoPriceCalc ?? 0;
};

/**
 * Returns the human-readable discount label for a promo (e.g. "Save 30%" or
 * "$30 OFF"). Used by product card tags and search topic badges so both
 * surfaces stay in sync with whatever discount is actually live.
 *
 * Returns an empty string when no displayable discount can be derived.
 */
export const getDiscountText = (promo: Promo, productPrice: number): string => {
  if (promo.discounts.flat_rate) {
    return `$${promo.discounts.flat_rate} OFF`;
  }

  if (promo.discounts.flat_percent) {
    return `Save ${promo.discounts.flat_percent}%`;
  }

  const tiers: Tier[] | null = promo.discounts.tiered;
  if (tiers && tiers.length > 0) {
    const bestTier = tiers.reduce((bestTier, tier) => {
      const [minPrice, maxPrice] = tier.price_range;
      if (
        productPrice >= minPrice &&
        (maxPrice === null || productPrice <= maxPrice) &&
        (!bestTier || tier.percent > bestTier.percent)
      ) {
        return tier;
      }
      return bestTier;
    }, null as Tier | null);

    if (bestTier) {
      return `Save ${bestTier.percent}%`;
    }
  }

  return '';
};
